import { useEffect, useMemo, useState } from "react";
import { collection, query, where, limit, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { converters, civicMetaRef } from "@/lib/firestore";

export interface CivicStudio {
  id: string;
  name: string;
}

interface StudioState {
  scope: string | null;
  studios: CivicStudio[];
}

interface CivicState {
  scopeKey: string | null;
  ids: Set<string>;
}

const EMPTY_STUDIOS: CivicStudio[] = [];
const EMPTY_CIVIC_IDS = new Set<string>();

function emptyStudioState(scope: string | null): StudioState {
  return { scope, studios: [] };
}

function emptyCivicState(scopeKey: string | null): CivicState {
  return { scopeKey, ids: new Set() };
}

/**
 * Resolves the org's studios that are actually set up as civic agents — i.e.
 * those whose `civic/meta` doc has a `pickaxeId`. Drives the per-studio "City
 * Administration" app entries.
 *
 * Only active when an orgId is present and the org has CivicOne assigned, so we
 * never query studios platform-wide (that would trip org Firestore rules and
 * pull every studio). Civic-enabled orgs realistically have a handful of
 * studios, so the per-studio `civic/meta` listeners stay cheap.
 */
export function useCivicStudios(orgId: string | undefined, enabled: boolean): CivicStudio[] {
  const requestedScope = enabled && orgId ? orgId : null;
  const [studioState, setStudioState] = useState<StudioState>(() =>
    emptyStudioState(requestedScope)
  );

  // 1) The org's studios.
  useEffect(() => {
    let cancelled = false;
    setStudioState(emptyStudioState(requestedScope));

    if (!requestedScope) {
      return;
    }
    // No orderBy — filtering by orgId alone avoids needing a composite index;
    // we sort client-side below.
    const q = query(
      collection(db, "studios").withConverter(converters.studios),
      where("orgId", "==", requestedScope),
      limit(200)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        if (cancelled) return;
        setStudioState({
          scope: requestedScope,
          studios: snap.docs.map((d) => ({ id: d.id, name: d.data().name })),
        });
      },
      (err) => {
        if (cancelled) return;
        console.error("useCivicStudios: error loading studios", err);
        setStudioState(emptyStudioState(requestedScope));
      }
    );
    return () => {
      cancelled = true;
      unsub();
    };
  }, [requestedScope]);

  // React renders a new scope before it tears down the old scope's effects.
  // Return an empty list for that render instead of the prior org's studios.
  const studios =
    studioState.scope === requestedScope ? studioState.studios : EMPTY_STUDIOS;

  // 2) Per-studio civic/meta presence. Serialize only the scope and studio IDs
  // so a name-only studio snapshot updates the returned label without tearing
  // down every metadata listener or temporarily hiding a known app.
  const civicScopeKey =
    requestedScope && studios.length > 0
      ? JSON.stringify([requestedScope, ...studios.map((studio) => studio.id)])
      : null;
  const [civicState, setCivicState] = useState<CivicState>(() =>
    emptyCivicState(civicScopeKey)
  );

  useEffect(() => {
    let cancelled = false;
    setCivicState(emptyCivicState(civicScopeKey));

    if (!civicScopeKey) {
      return;
    }
    const [, ...studioIds] = JSON.parse(civicScopeKey) as string[];
    const live = new Set<string>();
    const apply = () => {
      if (cancelled) return;
      setCivicState({ scopeKey: civicScopeKey, ids: new Set(live) });
    };
    const unsubs = studioIds.map((id) =>
      onSnapshot(
        civicMetaRef(id),
        (snap) => {
          if (cancelled) return;
          const pickaxeId = snap.exists() ? snap.data().pickaxeId : null;
          if (pickaxeId && pickaxeId.trim()) live.add(id);
          else live.delete(id);
          apply();
        },
        () => {
          if (cancelled) return;
          live.delete(id);
          apply();
        }
      )
    );
    return () => {
      cancelled = true;
      unsubs.forEach((unsub) => unsub());
    };
  }, [civicScopeKey]);

  const civicIds =
    civicState.scopeKey === civicScopeKey ? civicState.ids : EMPTY_CIVIC_IDS;

  return useMemo(
    () =>
      studios
        .filter((s) => civicIds.has(s.id))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [studios, civicIds]
  );
}
